import { supabase } from '../utils/supabase';

/**
 * Notification Service
 * Handles all notification-related operations for the guest account linking system
 */

/**
 * Create a notification (duplicate-safe)
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.user_id - User ID
 * @param {string} notificationData.type - Notification type
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {Object} notificationData.data - Additional data (JSONB)
 * @returns {Promise<Object>} - Created notification
 */
export const createNotification = async (notificationData) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .insert([{
        user_id: notificationData.user_id,
        type: notificationData.type,
        title: notificationData.title,
        message: notificationData.message,
        data: notificationData.data || null,
        is_read: false,
        is_archived: false
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[NotificationService] Error creating notification:', error);
    throw error;
  }
};

/**
 * Get user notifications (paginated)
 * @param {string} userId - User ID
 * @param {number} page - Page number (1-indexed)
 * @param {number} pageSize - Number of notifications per page (default: 20)
 * @param {Object} filters - Optional filters { is_read, is_archived, type }
 * @returns {Promise<Object>} - { notifications, total, page, pageSize, totalPages }
 */
export const getUserNotifications = async (userId, page = 1, pageSize = 20, filters = {}) => {
  try {
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.is_read !== undefined) {
      query = query.eq('is_read', filters.is_read);
    }
    if (filters.is_archived !== undefined) {
      query = query.eq('is_archived', filters.is_archived);
    }
    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    // Pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    const totalPages = Math.ceil((count || 0) / pageSize);

    return {
      notifications: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages
    };
  } catch (error) {
    console.error('[NotificationService] Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} - Updated notification
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', notificationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[NotificationService] Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Number of notifications updated
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('is_read', false)
      .select();

    if (error) throw error;
    return data?.length || 0;
  } catch (error) {
    console.error('[NotificationService] Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Get unread notification count (cached, max 100)
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Unread count (capped at 100)
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false)
      .eq('is_archived', false)
      .limit(100);  // Cap at 100 for performance

    if (error) throw error;
    return Math.min(count || 0, 100);
  } catch (error) {
    console.error('[NotificationService] Error getting unread count:', error);
    throw error;
  }
};

/**
 * Archive old notifications (read > 90 days)
 * @param {string} userId - User ID (optional, if not provided archives for all users)
 * @returns {Promise<number>} - Number of notifications archived
 */
export const archiveOldNotifications = async (userId = null) => {
  try {
    let query = supabase
      .from('notifications')
      .update({
        is_archived: true,
        archived_at: new Date().toISOString()
      })
      .eq('is_read', true)
      .eq('is_archived', false)
      .lt('read_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString());

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.select();

    if (error) throw error;
    return data?.length || 0;
  } catch (error) {
    console.error('[NotificationService] Error archiving old notifications:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
  } catch (error) {
    console.error('[NotificationService] Error deleting notification:', error);
    throw error;
  }
};

// ============================================================================
// Event-Driven Notification Functions (New Architecture)
// ============================================================================

/**
 * Create a manual notification event (for testing or explicit triggers)
 * @param {string} userId - User ID
 * @param {string} eventType - Event type: 'email_changed', 'guest_email_assigned', 'manual_check'
 * @param {Object} eventData - Event data payload
 * @returns {Promise<string>} - Event ID
 */
export const createNotificationEvent = async (userId, eventType, eventData) => {
  try {
    const { data, error } = await supabase.rpc('create_notification_event_manual', {
      p_user_id: userId,
      p_event_type: eventType,
      p_event_data: eventData
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[NotificationService] Error creating notification event:', error);
    throw error;
  }
};

/**
 * Process notification events using the database function
 * @param {number} batchSize - Number of events to process (default: 10, max: 50)
 * @returns {Promise<Object>} - Processing result
 */
export const processNotificationEvents = async (batchSize = 10) => {
  try {
    // Validate batch size
    const safeBatchSize = Math.min(Math.max(1, batchSize), 50);
    
    console.log('[NotificationService] Processing notification events via database function');
    console.log('[NotificationService] Batch size:', safeBatchSize);

    // Call the database function directly
    const { data, error } = await supabase.rpc('process_notification_events_batch', {
      p_batch_size: safeBatchSize
    });

    if (error) {
      console.error('[NotificationService] Error calling database function:', error);
      throw error;
    }

    console.log('[NotificationService] Processing result:', data);
    return data;
  } catch (error) {
    console.error('[NotificationService] Error processing notification events:', error);
    throw error;
  }
};

/**
 * Get notification event status (for monitoring/debugging)
 * @param {string} userId - User ID (optional, if not provided gets all events)
 * @param {Object} filters - Optional filters { processed, event_type }
 * @returns {Promise<Array>} - Array of events
 */
export const getNotificationEvents = async (userId = null, filters = {}) => {
  try {
    let query = supabase
      .from('notification_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100); // Limit for performance

    if (userId) {
      query = query.eq('user_id', userId);
    }

    if (filters.processed !== undefined) {
      query = query.eq('processed', filters.processed);
    }

    if (filters.event_type) {
      query = query.eq('event_type', filters.event_type);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('[NotificationService] Error getting notification events:', error);
    throw error;
  }
};

/**
 * Get unprocessed event count (for monitoring)
 * @returns {Promise<number>} - Count of unprocessed events
 */
export const getUnprocessedEventCount = async () => {
  try {
    const { count, error } = await supabase
      .from('notification_events')
      .select('*', { count: 'exact', head: true })
      .eq('processed', false)
      .lt('retry_count', 3); // Only count events that haven't exceeded max retries

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('[NotificationService] Error getting unprocessed event count:', error);
    throw error;
  }
};

